import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { safeReturnUrl } from './admin.guard';
import { AuthSessionService } from './auth-session.service';
import { ExternalNavigation } from './external-navigation.service';

/** Starts the hosted Cognito login, which returns to `returnTo` through `/auth/finish`. */
export function cognitoLoginUrl(returnTo: string | null | undefined): string {
  const url = new URL(`${environment.cognitoAuthUrl}/login`);
  url.searchParams.set('returnTo', loginReturnUrl(returnTo));
  return url.toString();
}

/** Auth pages are never a useful place to come back to after logging in. */
function loginReturnUrl(value: string | null | undefined): string {
  const url = safeReturnUrl(value);
  const path = url.split(/[?#]/, 1)[0];
  return path.startsWith('/auth/') || path === '/admin-login' ? '/' : url;
}

/** Login and sign-out buttons go through this service, which keeps them disabled while one is under way. */
@Injectable({ providedIn: 'root' })
export class CognitoLogin {
  private readonly session = inject(AuthSessionService);
  private readonly navigation = inject(ExternalNavigation);
  private readonly redirecting = signal(false);

  /** A login or sign-out is under way, so the buttons stay disabled. */
  readonly busy = computed(() => this.redirecting() || this.session.state().status === 'loading');

  constructor() {
    // A page restored from the back-forward cache would otherwise keep its buttons disabled.
    inject(DOCUMENT).defaultView?.addEventListener('pageshow', (event) => {
      if (event.persisted) this.redirecting.set(false);
    });
  }

  start(returnTo: string | null | undefined): void {
    if (this.redirecting()) return;
    this.redirecting.set(true);
    this.navigation.assign(cognitoLoginUrl(returnTo));
  }

  /**
   * Ends the Firebase and Cognito sessions whatever the identity source. Cognito
   * answers a login from its existing session, so this is the way out when that
   * session keeps failing. The sign-out returns to Cognito's sign-out URL, not the app.
   */
  async signOut(): Promise<void> {
    if (this.redirecting()) return;
    this.redirecting.set(true);
    await this.session.clearLocalSession();
    this.navigation.assign(`${environment.cognitoAuthUrl}/logout`);
  }
}
